// SPDX-License-Identifier: AGPL-3.0-or-later

import i18n from '@app/app/I18n';
import Authentication from '@app/features/auth/state/Authentication';
import * as ToastCommands from '@app/features/ui/commands/ToastCommands';
import MediaEngine from '@app/features/voice/engine/MediaEngineFacade';
import type {GatewayCallData} from '@app/features/voice/state/CallState';
import {msg} from '@lingui/core/macro';

const ALONE_NOTICE_DESCRIPTOR = msg({
	message: "You're the only one in this call. You'll be disconnected in 3 minutes if no one joins.",
	comment: 'Toast shown when everyone else has left a direct or group call. The gateway disconnects after 3 minutes.',
});
const ALONE_NOTICE_TIMEOUT_MS = 10_000;

const noticedChannelIds = new Set<string>();

export function isAloneInDirectCall(call: GatewayCallData, userId: string | null): boolean {
	if (!userId || (call.ringing?.length ?? 0) > 0) return false;
	const userIds = new Set((call.voice_states ?? []).map((voiceState) => voiceState.user_id));
	return userIds.size === 1 && userIds.has(userId);
}

export function syncDirectCallAloneNotice(call: GatewayCallData): void {
	const alone =
		MediaEngine.channelId === call.channel_id && isAloneInDirectCall(call, Authentication.currentUserId ?? null);
	if (!alone) {
		noticedChannelIds.delete(call.channel_id);
		return;
	}
	if (noticedChannelIds.has(call.channel_id)) return;
	noticedChannelIds.add(call.channel_id);
	ToastCommands.createToast({
		type: 'info',
		children: i18n._(ALONE_NOTICE_DESCRIPTOR),
		timeout: ALONE_NOTICE_TIMEOUT_MS,
	});
}

export function clearDirectCallAloneNotice(channelId: string): void {
	noticedChannelIds.delete(channelId);
}
